import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(to right, #2D3142 70%, #7ECBB9 100%);
  color: white;
  padding: 6rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 650px;
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const PracticalBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--secondary);
`;

const Heading = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  span {
    color: var(--secondary);
    position: relative;
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  line-height: 1.6;
  font-weight: 300;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--secondary);
  color: #2D3142;
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: white;
    color: var(--secondary);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const HeroImageContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: perspective(1000px) rotateY(-5deg);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: perspective(1000px) rotateY(0);
  }
`;

const ComparisonSection = styled.section`
  padding: 6rem 0;
  background-color: white;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #2D3142;
  text-align: center;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background-color: var(--secondary);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #6C757D;
  margin: 2rem auto 3rem;
  text-align: center;
  max-width: 800px;
`;

const ComparisonTable = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  
  .table-header {
    background-color: #2D3142;
    color: white;
    padding: 1.5rem;
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    
    @media (max-width: 768px) {
      padding: 1rem;
    }
    
    .header-cell {
      font-weight: 700;
      font-size: 1.1rem;
      
      &:first-child {
        text-align: left;
      }
      
      &:not(:first-child) {
        text-align: center;
      }
      
      &.highlight {
        color: var(--secondary);
      }
    }
  }
  
  .table-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
      border-bottom: none;
    }
    
    .cell {
      padding: 1.25rem 1.5rem;
      
      @media (max-width: 768px) {
        padding: 1rem;
      }
      
      &:first-child {
        text-align: left;
        font-weight: 500;
      }
      
      &:not(:first-child) {
        text-align: center;
        
        .check {
          color: var(--secondary);
          font-size: 1.2rem;
        }
        
        .x {
          color: #dc3545;
          font-size: 1.2rem;
        }
      }
      
      &.price {
        font-weight: 700;
        font-size: 1.2rem;
        
        &.highlight {
          color: var(--secondary);
        }
      }
    }
  }
`;

const FeaturesSection = styled.section`
  padding: 6rem 0;
  background-color: #f8f9fa;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  height: 100%;
  
  .icon {
    width: 60px;
    height: 60px;
    background-color: rgba(126, 203, 185, 0.1);
    color: var(--secondary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    font-size: 1.5rem;
  }
  
  h3 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #2D3142;
  }
  
  p {
    color: #6C757D;
    margin-bottom: 0;
    line-height: 1.6;
  }
`;

const FAQSection = styled.section`
  padding: 6rem 0;
  background-color: white;
`;

const FAQItem = styled.div`
  margin-bottom: 1.5rem;
  
  .question {
    font-size: 1.2rem;
    font-weight: 700;
    color: #2D3142;
    margin-bottom: 0.75rem;
  }
  
  .answer {
    color: #6C757D;
    line-height: 1.6;
  }
`;

const CTASection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #7ECBB9 0%, #65A395 100%);
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  span {
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: -1;
    }
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--secondary);
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: #2D3142;
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const GiftLandingPagePractical = () => {
  // Google Analytics tracking code setup
  useEffect(() => {
    // Track page view for this landing page
    ReactGA.send({
      hitType: "pageview",
      page: "/gift-landing-practical",
      title: "Gift Landing Page - Practical"
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      ReactGA.event({
        category: 'engagement',
        action: 'gift_cta_click',
        label: 'Gift Landing Page Practical CTA',
        value: 1
      });
    };
    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.gift-cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
    };
  }, []);

  return (
    <LandingContainer>
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <HeroContent>
                <PracticalBadge>
                  <i className="bi bi-gift me-2"></i>
                  PRACTICAL GIFT FOR NEW PARENTS
                </PracticalBadge>
                <Heading>
                  A <span>SMARTER GIFT</span> THAN ANOTHER BABY OUTFIT
                </Heading>
                <SubHeading>
                  Give a gift that solves real problems. Our AI-powered sleep plans offer personalized, evidence-based solutions that last longer than any physical gift.
                </SubHeading>
                <ButtonWrapper>
                  <CtaButton to="/gift" className="gift-cta-button">
                    PURCHASE A GIFT PLAN
                  </CtaButton>
                </ButtonWrapper>
              </HeroContent>
            </div>
            <div className="col-lg-5">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/helena-lopes-o36DEkDgOCk-unsplash.jpg" 
                  alt="Baby sleeping peacefully" 
                />
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <ComparisonSection>
        <div className="container">
          <SectionTitle>VALUE COMPARISON</SectionTitle>
          <SectionSubtitle>
            See how our gift sleep plans compare to other common baby gifts and sleep solutions.
          </SectionSubtitle>
          
          <ComparisonTable>
            <div className="table-header">
              <div className="header-cell">Option</div>
              <div className="header-cell highlight">Dozzy Gift Plan</div>
              <div className="header-cell">Traditional Gifts</div>
            </div>
            
            <div className="table-row">
              <div className="cell">Price</div>
              <div className="cell price highlight">$49 - $79</div>
              <div className="cell price">$50 - $200+</div>
            </div>
            
            <div className="table-row">
              <div className="cell">Personalized to child</div>
              <div className="cell"><i className="bi bi-check-circle-fill check"></i></div>
              <div className="cell"><i className="bi bi-x-circle-fill x"></i></div>
            </div>
            
            <div className="table-row">
              <div className="cell">Evidence-based approach</div>
              <div className="cell"><i className="bi bi-check-circle-fill check"></i></div>
              <div className="cell"><i className="bi bi-x-circle-fill x"></i></div>
            </div>
            
            <div className="table-row">
              <div className="cell">Solves real problems</div>
              <div className="cell"><i className="bi bi-check-circle-fill check"></i></div>
              <div className="cell"><i className="bi bi-x-circle-fill x"></i></div>
            </div>
            
            <div className="table-row">
              <div className="cell">Long-lasting value</div>
              <div className="cell"><i className="bi bi-check-circle-fill check"></i></div>
              <div className="cell"><i className="bi bi-x-circle-fill x"></i></div>
            </div>
            
            <div className="table-row">
              <div className="cell">Digital delivery</div>
              <div className="cell"><i className="bi bi-check-circle-fill check"></i></div>
              <div className="cell"><i className="bi bi-x-circle-fill x"></i></div>
            </div>
          </ComparisonTable>
        </div>
      </ComparisonSection>

      <FeaturesSection>
        <div className="container">
          <SectionTitle>PRACTICAL BENEFITS</SectionTitle>
          <SectionSubtitle>
            Our gift sleep plans offer real solutions to common challenges faced by new parents.
          </SectionSubtitle>
          
          <FeatureGrid>
            <FeatureCard>
              <div className="icon">
                <i className="bi bi-clock"></i>
              </div>
              <h3>Time-Saving</h3>
              <p>
                Parents save an average of 14 hours per week researching sleep solutions. Our AI analyzes trusted resources and creates a personalized plan instantly.
              </p>
            </FeatureCard>
            
            <FeatureCard>
              <div className="icon">
                <i className="bi bi-graph-up"></i>
              </div>
              <h3>Cost-Effective</h3>
              <p>
                At $49 (single child) or $79 (two children), our plans cost a fraction of traditional sleep consultants ($300-$600) while providing personalized guidance.
              </p>
            </FeatureCard>
            
            <FeatureCard>
              <div className="icon">
                <i className="bi bi-shield-check"></i>
              </div>
              <h3>Evidence-Based</h3>
              <p>
                All recommendations follow guidelines from respected Australian organizations like Red Nose Australia and Raising Children Network.
              </p>
            </FeatureCard>
            
            <FeatureCard>
              <div className="icon">
                <i className="bi bi-gear"></i>
              </div>
              <h3>Customizable</h3>
              <p>
                Plans adapt to the child's age, sleep patterns, and parents' preferred approach, ensuring a solution that fits their specific situation.
              </p>
            </FeatureCard>
            
            <FeatureCard>
              <div className="icon">
                <i className="bi bi-envelope"></i>
              </div>
              <h3>Instant Delivery</h3>
              <p>
                Gift recipients receive immediate access to create their sleep plan—no shipping delays or physical packaging waste.
              </p>
            </FeatureCard>
            
            <FeatureCard>
              <div className="icon">
                <i className="bi bi-arrow-repeat"></i>
              </div>
              <h3>Adaptable</h3>
              <p>
                As the child grows and sleep needs change, parents can update information and generate revised plans at no additional cost.
              </p>
            </FeatureCard>
          </FeatureGrid>
        </div>
      </FeaturesSection>

      <FAQSection>
        <div className="container">
          <SectionTitle>COMMON QUESTIONS</SectionTitle>
          <SectionSubtitle>
            Everything you need to know about giving a Dozzy sleep plan as a gift.
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="row">
                <div className="col-md-6">
                  <FAQItem>
                    <p className="question">How does the gift process work?</p>
                    <p className="answer">
                      You'll purchase a gift plan, add a personal message, and provide the recipient's email. They'll receive instructions to create their personalized sleep plan.
                    </p>
                  </FAQItem>
                  
                  <FAQItem>
                    <p className="question">What if they already have a sleep plan?</p>
                    <p className="answer">
                      If the recipient already has a Dozzy account with a sleep plan, the gift will extend their subscription and allow them to create additional plans.
                    </p>
                  </FAQItem>
                </div>
                
                <div className="col-md-6">
                  <FAQItem>
                    <p className="question">How long does the gift last?</p>
                    <p className="answer">
                      Gift plans provide 12 months of access, allowing recipients to create and update their sleep plans as their child grows and sleep needs change.
                    </p>
                  </FAQItem>
                  
                  <FAQItem>
                    <p className="question">What ages are the sleep plans suitable for?</p>
                    <p className="answer">
                      Our sleep plans are designed for children from 0-5 years old, with age-specific recommendations that follow developmental guidelines.
                    </p>
                  </FAQItem>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </FAQSection>

      <CTASection>
        <div className="container">
          <CTATitle>
            GIVE A <span>PRACTICAL GIFT</span> THEY'LL ACTUALLY USE
          </CTATitle>
          <CTADescription>
            Skip the onesies and toys. Give the gift of sleep—a solution that makes a real difference in a family's daily life.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/gift" className="gift-cta-button">
              PURCHASE A GIFT PLAN
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default GiftLandingPagePractical;
